import { useState, type ChangeEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  Button,
  Checkbox,
  Divider,
  Input,
  Modal,
  Tooltip,
  Typography,
} from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import {
  MIN_MINUTES,
  MAX_MINUTES,
  isValidMinutes,
} from '../../../../constants/customArea';
import type { PolygonType } from '../../../../types/domain';
import { useInstructionsVisibility } from '../../../../hooks/SimViewer/useInstructionsVisibility';
import { useCustomAreaCreation } from '../../../../hooks/SimViewer/useCustomAreaCreation';

type Props = {
  customAreaType: PolygonType | null;
  isOpen: boolean;
};

const CustomAreaCreateModal = ({
  customAreaType,
  isOpen,
}: Props): ReactNode => {
  const { t } = useTranslation();
  const { instructionsVisibility, toggleInstructionsVisibility } =
    useInstructionsVisibility();
  const { creatingCustomArea, editCustomArea, endCustomAreaCreation } =
    useCustomAreaCreation();

  const [newMinutes, setNewMinutes] = useState<string>(
    String(creatingCustomArea?.minutes || '')
  );

  const instructions = customAreaType
    ? t(`message.${customAreaType}CustomAreaCreatingInstructions`, {
        returnObjects: true,
      })
    : [];

  return (
    <Modal
      open={isOpen}
      destroyOnClose
      closable={false}
      mask={false}
      maskClosable={false}
      keyboard={false}
      footer={null}
      width="fit-content"
      style={{
        top: instructionsVisibility.createCustomArea ? '75vh' : '80vh',
        padding: 0,
        minWidth: 400,
      }}
      wrapProps={{ style: { pointerEvents: 'none' } }}
      bodyStyle={{ paddingInline: 16, paddingBlock: 8, borderRadius: 8 }}
    >
      {customAreaType && (
        <>
          <RowContainer>
            <Typography.Text
              style={{ flexShrink: 0, flexGrow: 1, marginRight: 16 }}
            >
              {t('creatingCustomArea', {
                type: t(
                  customAreaType === 'walking'
                    ? 'walkingDistance'
                    : customAreaType
                ),
              })}
            </Typography.Text>
            <Button
              type="default"
              shape="round"
              icon={<CloseOutlined />}
              style={{ color: '#ff4d4f', borderColor: '#ff4d4f' }}
              onClick={endCustomAreaCreation}
            >
              {t('cancel')}
            </Button>
            {creatingCustomArea?.type === 'walking' && (
              <>
                {t('travelMode.walk')}
                <Tooltip
                  open={isOpen && !isValidMinutes(newMinutes)}
                  title={t('message.invalidWalkingMins', {
                    min: MIN_MINUTES,
                    max: MAX_MINUTES,
                  })}
                >
                  <Input
                    type="number"
                    name="minutes"
                    value={newMinutes}
                    min={MIN_MINUTES}
                    max={MAX_MINUTES}
                    step="1"
                    autoFocus
                    style={{ width: 60, padding: '4px 8px' }}
                    status={isValidMinutes(newMinutes) ? undefined : 'error'}
                    onChange={(event: ChangeEvent<HTMLInputElement>): void => {
                      const value = event.currentTarget.value.trim();

                      setNewMinutes(value);

                      if (!Number.isNaN(Number(value))) {
                        editCustomArea({ minutes: Number(value) });
                      }
                    }}
                  />
                </Tooltip>
                {t('unit.mins')}
              </>
            )}
          </RowContainer>
          {instructionsVisibility.createCustomArea && (
            <ColumnContainer>
              <Divider
                type="horizontal"
                style={{
                  margin: '4px 0',
                  borderColor: 'var(--primary-color)',
                  opacity: 0.3,
                }}
              />
              <RowContainer>
                <Label>{t('instructions')}</Label>
                <StyledCheckbox
                  defaultChecked={false}
                  onChange={(): void => {
                    toggleInstructionsVisibility('createCustomArea');
                  }}
                >
                  {t('dontShowAgain')}
                </StyledCheckbox>
              </RowContainer>
              <NumberList>
                {instructions.map((line: string, index: number): ReactNode => {
                  return <ListItem key={index}>{line}</ListItem>;
                })}
              </NumberList>
            </ColumnContainer>
          )}
        </>
      )}
    </Modal>
  );
};

const RowContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  gap: 8px;
`;

const ColumnContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 4px;
`;

const Label = styled.span`
  margin-left: 8px;
  font-size: 12px;
  font-weight: 600;
`;

const StyledCheckbox = styled(Checkbox)`
  margin-left: auto;
  font-size: 12px;
  line-height: 12px;
  display: flex;
  flex-direction: row;
  align-items: center;

  .ant-checkbox {
    top: 0;
  }

  .ant-checkbox + span {
    padding: 0;
    margin-left: 4px;
  }
`;

const NumberList = styled.ol`
  counter-reset: item;
  list-style-type: none;
  font-size: 14px;
  padding: 0;
  margin: 0 0 0 4px;
`;

const ListItem = styled.li`
  counter-increment: item;
  position: relative;
  padding-left: 24px;
  margin-top: 4px;

  &::before {
    content: counter(item);
    width: 18px;
    height: 18px;
    font-size: 11px;
    font-weight: 600;
    background: #8c8c8c;
    color: #ffffff;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 2px;
    left: 0;
  }
`;

export { CustomAreaCreateModal };
